/**
 * Lightweight chain metadata map for the x402 payment page.
 *
 * Keyed by the decimal `chainId` that `/info` returns — a small
 * dedicated table here avoids pulling the full chain-config graph
 * from `chainsStaticObject/` (which drags viem chain definitions +
 * RPC URLs + icon bundles into the public /pay/* route). A stale
 * chainId just means the explorer link + switchChain prompt show
 * a generic label instead of the branded name — the payment itself
 * still works via wagmi's own chain config from App.js.
 *
 * Keep in sync with backend `src/constants/networks.ts::CHAIN_IDS`
 * — the backend is the source of truth; this mirror is display-only.
 */

export const PAYMENT_CHAINS = {
  // Ethereum
  1: {
    name: "Ethereum",
    explorer: "https://etherscan.io",
    shortName: "ETH",
  },
  11155111: {
    name: "Sepolia",
    explorer: "https://sepolia.etherscan.io",
    shortName: "Sepolia",
  },
  // Base
  8453: {
    name: "Base",
    explorer: "https://basescan.org",
    shortName: "Base",
  },
  84532: {
    name: "Base Sepolia",
    explorer: "https://sepolia.basescan.org",
    shortName: "Base Sepolia",
  },
  // Polygon
  137: {
    name: "Polygon",
    explorer: "https://polygonscan.com",
    shortName: "Polygon",
  },
  80002: {
    name: "Polygon Amoy",
    explorer: "https://amoy.polygonscan.com",
    shortName: "Amoy",
  },
  // BSC
  56: {
    name: "BNB Smart Chain",
    explorer: "https://bscscan.com",
    shortName: "BSC",
  },
  97: {
    name: "BSC Testnet",
    explorer: "https://testnet.bscscan.com",
    shortName: "BSC Testnet",
  },
  // Arbitrum
  42161: {
    name: "Arbitrum",
    explorer: "https://arbiscan.io",
    shortName: "Arbitrum",
  },
  421614: {
    name: "Arbitrum Sepolia",
    explorer: "https://sepolia.arbiscan.io",
    shortName: "Arb Sepolia",
  },
  // Avalanche
  43114: {
    name: "Avalanche",
    explorer: "https://snowtrace.io",
    shortName: "AVAX",
  },
  43113: {
    name: "Avalanche Fuji",
    explorer: "https://testnet.snowtrace.io",
    shortName: "Fuji",
  },
};

/** Resolve display name for a chainId. Falls back to "Chain #<id>" so
 *  the UI never renders `undefined` for a chain we haven't mapped. */
export function getChainName(chainId) {
  if (!chainId) return "Unknown chain";
  return PAYMENT_CHAINS[chainId]?.name || `Chain #${chainId}`;
}

/** Explorer URL for an address on a given chain. Used by the
 *  "Verify on explorer" link next to the creator address. Returns
 *  null when we don't have an explorer mapping — caller hides the
 *  link rather than rendering a broken one. */
export function getAddressExplorerUrl(chainId, address) {
  const explorer = PAYMENT_CHAINS[chainId]?.explorer;
  if (!explorer || !address) return null;
  return `${explorer}/address/${address}`;
}

/** Explorer URL for a transaction hash. Used on the receipt card. */
export function getTxExplorerUrl(chainId, txHash) {
  const explorer = PAYMENT_CHAINS[chainId]?.explorer;
  if (!explorer || !txHash || txHash === "pending") return null;
  return `${explorer}/tx/${txHash}`;
}
